import {
  faArrowRight,
  faBuildingUser,
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
  faClock,
  faPhone,
  faUser,
  faUserSecret
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { useContext, useMemo, useState } from 'react'
import { useCurrentUserInfos } from '~/api/queries/user'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { HoverInfo } from '~/components/Global/Elements/HoverInfo/HoverInfo'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useActions } from '~/domains/actions/queries/actions'
import { type Action, ActionType } from '~/domains/actions/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { useDashboardContext } from '~/domains/dashboard/context/DashboardContext'
import type { DashboardBlockLayoutProps } from '~/domains/dashboard/types'
import { UsersList } from '~/domains/users/components/UsersList'
import { ApplicantModalContext } from '~/utils/contextes'
import { formatDuration, formatTime } from '~/utils/date'
import { formatName } from '~/utils/names'
import { transformUrlIntoLink } from '~/utils/urls'
import { DashboardBlockLayout } from '../../DashboardBlock/DashboardBlockLayout'
import {
  actionHoverHeader,
  actionHoverHeaderInfo,
  actionHoverHeaderName,
  actionHoverHeaderNamePlaceholder,
  actionHoverList,
  actionHoverListItem,
  actionHoverListItemButton,
  actionHoverListItemLabel,
  actionHoverNote,
  actionHoverNoteContent,
  calendarContainer,
  currentTimeIndicator,
  dayColumn,
  dayDate,
  dayDateMonth,
  dayHeader,
  dayNumber,
  eventBlock,
  eventBlockDetails,
  eventBlockIcon,
  eventBlockTime,
  eventBlockTitle,
  periodDisplay,
  timeSlot,
  timeSlotLabel,
  timeSlotLast,
  timetable,
  timetableContainer
} from './CalendarBlock.css'
import { CalendarConfigDropdown } from './CalendarConfigDropdown'

type ActionWithPosition = {
  action: Action
  actionDate: Date
  actionEndDate: Date | null
  topPercent: number
  bottomPercent: number
  height: number
}

const formatMonth = (date: Date): string => {
  return date.toLocaleDateString('fr-FR', { month: 'long' })
}

const formatPeriod = (date1: Date, date2: Date): string => {
  const day1 = date1.getDate()
  const month1 = date1.toLocaleDateString('fr-FR', { month: 'short' })
  const day2 = date2.getDate()
  const month2 = date2.toLocaleDateString('fr-FR', { month: 'short' })
  return `${day1} ${month1} - ${day2} ${month2}`
}

const getTimePosition = (
  hour: number,
  minute: number,
  startHour: number,
  endHour: number
): number => {
  const totalMinutes = hour * 60 + minute
  const startMinutes = startHour * 60
  const endMinutes = endHour * 60
  const totalRange = endMinutes - startMinutes
  const position = ((totalMinutes - startMinutes) / totalRange) * 100
  return Math.max(0, Math.min(100, position))
}

export const getIconForActionType = (action: Action) => {
  if (action.isPrivate) {
    return <FontAwesomeIcon icon={faUserSecret} className={eventBlockIcon} />
  }
  if (action.type === ActionType.CALL) {
    return <FontAwesomeIcon icon={faPhone} className={eventBlockIcon} />
  }
  if (
    action.type === ActionType.MEETING ||
    action.type === ActionType.MEETING_CONTACT
  ) {
    return <FontAwesomeIcon icon={faCalendarDays} className={eventBlockIcon} />
  }
  return null
}

type ActionEventBlockProps = {
  action: Action
  actionDate: Date
  actionEndDate: Date | null
  topPercent: number
  height: number
  left: number | string
  width: number | string
  totalHeight: number
  user: { id: number } | null
  legacyUrl: string
}

const ActionEventBlock = ({
  action,
  actionDate,
  actionEndDate,
  topPercent,
  height,
  left,
  width,
  totalHeight,
  user,
  legacyUrl
}: ActionEventBlockProps): ReactElement => {
  const [refElement, setRefElement] = useState<HTMLDivElement | null>(null)
  const applicants = Object.values(action.applicants)
  const contacts = Object.values(action.contacts)
  let title = action.subject
  const { setApplicantCollection, setApplicantCurrentId } = useContext(
    ApplicantModalContext
  )
  if (!title) {
    title =
      applicants.length > 0
        ? applicants.map((a) => formatName(a)).join(', ')
        : contacts.length > 0
          ? contacts.map((c) => formatName(c)).join(', ')
          : ''
  }

  const duration = actionEndDate
    ? formatDuration(actionDate, actionEndDate)
    : ''

  const handleApplicantClick = (applicantId: number) => {
    setApplicantCollection(applicants.map((a) => ({ id: a.id })))
    setApplicantCurrentId(applicantId)
  }

  return (
    <>
      <div
        ref={setRefElement}
        style={{
          top: `${(topPercent / 100) * totalHeight}px`,
          height: `${height}px`,
          left: typeof left === 'number' ? `${left}px` : left,
          width: typeof width === 'number' ? `${width}px` : width
        }}
        className={eventBlock}
      >
        <div className={eventBlockDetails}>
          <span className={eventBlockTitle}>
            {getIconForActionType(action)}
            {title}
          </span>
          <span className={eventBlockTime}>
            {formatTime(actionDate)}
            {actionEndDate && (
              <>
                <FontAwesomeIcon icon={faArrowRight} />
                {formatTime(actionEndDate)}
              </>
            )}
          </span>
        </div>
        {action.user && action.user.id !== user?.id && (
          <UsersList users={[action.user]} />
        )}
      </div>
      <HoverInfo linkedRef={refElement} delay={100}>
        <div className={actionHoverHeader}>
          <div className={actionHoverHeaderInfo}>
            <span className={actionHoverHeaderName}>
              {getIconForActionType(action)}
              {action.subject ? (
                action.subject
              ) : (
                <span className={actionHoverHeaderNamePlaceholder}>
                  Aucun objet
                </span>
              )}
            </span>
          </div>
        </div>
        <div className={actionHoverList}>
          <div className={actionHoverListItem}>
            <FontAwesomeIcon icon={faClock} />
            {formatTime(actionDate)}
            {actionEndDate && (
              <>
                {' '}
                <FontAwesomeIcon icon={faArrowRight} />{' '}
                {formatTime(actionEndDate)}
                {duration && ` (${duration})`}
              </>
            )}
          </div>

          {applicants.map((applicant) => (
            <div className={actionHoverListItem} key={applicant.id}>
              <Button
                variation={ButtonVariation.Link}
                buttonSize="extraSmall"
                narrow
                className={actionHoverListItemButton}
                onClick={() => {
                  handleApplicantClick(applicant.id)
                }}
              >
                <FontAwesomeIcon icon={faUser} />
                <span className={actionHoverListItemLabel}>
                  {formatName(applicant)}
                </span>
              </Button>
            </div>
          ))}
          {contacts.map((contact) => (
            <div className={actionHoverListItem} key={contact.id}>
              <Button
                href={`${legacyUrl}/admin/entreprises/edit/${contact.company?.id ?? 0}#contacts/${contact.id}`}
                variation={ButtonVariation.Link}
                buttonSize="extraSmall"
                narrow
                className={actionHoverListItemButton}
              >
                <FontAwesomeIcon icon={faBuildingUser} />
                <span className={actionHoverListItemLabel}>
                  {formatName(contact)}
                  {contact.company?.name && ` (${contact.company.name})`}
                </span>
              </Button>
            </div>
          ))}
        </div>
        {action.notes && (
          <div className={actionHoverNote}>
            <div className={actionHoverNoteContent}>
              {transformUrlIntoLink(action.notes)}
            </div>
          </div>
        )}
        {action.user && action.user.id !== user?.id && (
          <UsersList users={[action.user]} />
        )}
      </HoverInfo>
    </>
  )
}

export const CalendarBlock = (
  props: DashboardBlockLayoutProps
): ReactElement => {
  const [currentDateOffset, setCurrentDateOffset] = useState(0)
  const { userIds, isUserIdsLoading } = useDashboardContext()
  const { data: usersData } = useCurrentUserInfos()
  const user = usersData?.[0] ?? null
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')

  const { data: startHour } = useConfig('dashboard.calendar_start_hour')
  const { data: endHour } = useConfig('dashboard.calendar_end_hour')

  const startHourValue = startHour ?? 8
  const endHourValue = endHour ?? 19

  const currentDate = new Date()
  const firstDay = useMemo(() => {
    const date = new Date(currentDate)
    date.setDate(date.getDate() + currentDateOffset)
    date.setHours(0, 0, 0, 0)
    return date
  }, [currentDateOffset, currentDate])

  const secondDay = useMemo(() => {
    const date = new Date(firstDay)
    date.setDate(date.getDate() + 1)
    return date
  }, [firstDay])

  const scheduledDateParams = useMemo(() => {
    const start = new Date(firstDay)
    start.setHours(0, 0, 0, 0)

    const end = new Date(secondDay)
    end.setHours(23, 59, 59, 999)

    return {
      after: start.toISOString(),
      before: end.toISOString()
    }
  }, [firstDay, secondDay])

  const { data, isLoading, isError } = useActions({
    user: userIds,
    types: ['meeting'],
    itemsPerPage: 100,
    isCompleted: null,
    includePrivate: true,
    scheduledDate: scheduledDateParams,
    enabled:
      userIds.length > 0 &&
      !isUserIdsLoading &&
      startHour !== undefined &&
      endHour !== undefined
  })

  const allActions = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap((page) => page['hydra:member'])
  }, [data])

  const actionsByDay = useMemo(() => {
    const day1Actions: typeof allActions = []
    const day2Actions: typeof allActions = []

    allActions.forEach((action) => {
      const actionDate = new Date(action.eventDate)
      const actionHour = actionDate.getHours()
      const actionMinute = actionDate.getMinutes()
      const actionStartMinutes = actionHour * 60 + actionMinute

      // Get action end time
      const actionEndDate = action.scheduledDateEnd
        ? new Date(action.scheduledDateEnd)
        : null
      const actionEndMinutes = actionEndDate
        ? actionEndDate.getHours() * 60 + actionEndDate.getMinutes()
        : actionStartMinutes + 60 // Default 1 hour duration

      // Calculate configured hour range in minutes
      const configStartMinutes = startHourValue * 60
      const configEndMinutes = endHourValue * 60

      // Check if action overlaps with configured time range
      // Action overlaps if: action starts before config ends AND action ends after config starts
      const overlaps =
        actionStartMinutes < configEndMinutes &&
        actionEndMinutes > configStartMinutes

      if (!overlaps) {
        return
      }

      if (
        actionDate.getDate() === firstDay.getDate() &&
        actionDate.getMonth() === firstDay.getMonth() &&
        actionDate.getFullYear() === firstDay.getFullYear()
      ) {
        day1Actions.push(action)
      } else if (
        actionDate.getDate() === secondDay.getDate() &&
        actionDate.getMonth() === secondDay.getMonth() &&
        actionDate.getFullYear() === secondDay.getFullYear()
      ) {
        day2Actions.push(action)
      }
    })

    return { day1: day1Actions, day2: day2Actions }
  }, [allActions, firstDay, secondDay, startHourValue, endHourValue])

  const timeSlots = useMemo(() => {
    const slots: number[] = []
    for (let hour = startHourValue; hour < endHourValue; hour++) {
      slots.push(hour)
    }
    return slots
  }, [startHourValue, endHourValue])

  const getCurrentTimePosition = (day: Date): number | null => {
    const now = new Date()
    if (
      now.getDate() === day.getDate() &&
      now.getMonth() === day.getMonth() &&
      now.getFullYear() === day.getFullYear() &&
      now.getHours() >= startHourValue &&
      now.getHours() < endHourValue
    ) {
      return getTimePosition(
        now.getHours(),
        now.getMinutes(),
        startHourValue,
        endHourValue
      )
    }
    return null
  }

  const handlePrevious = () => {
    setCurrentDateOffset((prev) => prev - 1)
  }

  const handleNext = () => {
    setCurrentDateOffset((prev) => prev + 1)
  }

  const actions = (
    <div>
      <ButtonGroup>
        {(startHour !== undefined || endHour !== undefined) && (
          <CalendarConfigDropdown />
        )}

        <Button
          variation={ButtonVariation.Neutral}
          onClick={handlePrevious}
          aria-label="Jour précédent"
          buttonSize="extraSmall"
          narrow
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <span className={periodDisplay}>
          {formatPeriod(firstDay, secondDay)}
        </span>
        <Button
          variation={ButtonVariation.Neutral}
          onClick={handleNext}
          aria-label="Jour suivant"
          buttonSize="extraSmall"
          narrow
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
        <Button
          href={`${legacyUrl}/admin/agenda`}
          buttonSize="extraSmall"
          narrow
        >
          <FontAwesomeIcon icon={faCalendarDays} />
        </Button>
      </ButtonGroup>
    </div>
  )

  const renderDayTimetable = (
    day: Date,
    dayActions: typeof allActions
  ): ReactElement => {
    const currentTimePos = getCurrentTimePosition(day)
    const totalHeight = (endHourValue - startHourValue) * 30

    return (
      <div className={dayColumn}>
        <div className={dayHeader}>
          <span className={dayNumber}>{day.getDate()}</span>
          <span className={dayDate}>
            <span className={dayDateMonth}>{formatMonth(day)}</span>
            <span>{day.getFullYear()}</span>
          </span>
        </div>
        <div className={timetableContainer}>
          <div className={timetable} style={{ height: `${totalHeight}px` }}>
            {timeSlots.map((hour) => (
              <div
                key={hour}
                className={clsx(
                  timeSlot,
                  hour === timeSlots[timeSlots.length - 1] && timeSlotLast
                )}
                style={{
                  top: `${(hour - startHourValue) * 30}px`
                }}
              >
                <span className={timeSlotLabel}>{hour}h</span>
              </div>
            ))}
            {currentTimePos !== null && (
              <div
                className={currentTimeIndicator}
                style={{ top: `${(currentTimePos / 100) * totalHeight}px` }}
              />
            )}
            {(() => {
              // Calculate positions and overlaps for all actions
              const actionsWithPositions: ActionWithPosition[] = dayActions.map(
                (action) => {
                  const actionDate = new Date(action.eventDate)
                  const actionEndDate = action.scheduledDateEnd
                    ? new Date(action.scheduledDateEnd)
                    : null
                  const actionHour = actionDate.getHours()
                  const actionMinute = actionDate.getMinutes()
                  const topPercent = getTimePosition(
                    actionHour,
                    actionMinute,
                    startHourValue,
                    endHourValue
                  )

                  const minBottomPercent = getTimePosition(
                    actionHour + 1,
                    actionMinute,
                    startHourValue,
                    endHourValue
                  )
                  const bottomPercent = actionEndDate
                    ? Math.max(
                        minBottomPercent,
                        getTimePosition(
                          actionEndDate.getHours(),
                          actionEndDate.getMinutes(),
                          startHourValue,
                          endHourValue
                        )
                      )
                    : minBottomPercent
                  const height = Math.max(
                    30,
                    ((bottomPercent - topPercent) / 100) * totalHeight
                  )

                  return {
                    action,
                    actionDate,
                    actionEndDate,
                    topPercent,
                    bottomPercent,
                    height
                  }
                }
              )

              // Group overlapping actions
              const groups: ActionWithPosition[][] = []
              actionsWithPositions.forEach((actionPos) => {
                let addedToGroup = false
                for (const group of groups) {
                  const overlaps = group.some((groupAction) => {
                    return (
                      (actionPos.topPercent < groupAction.bottomPercent &&
                        actionPos.bottomPercent >= groupAction.topPercent) ||
                      (groupAction.topPercent < actionPos.bottomPercent &&
                        groupAction.bottomPercent >= actionPos.topPercent) ||
                      (actionPos.topPercent === groupAction.topPercent &&
                        actionPos.bottomPercent === groupAction.bottomPercent)
                    )
                  })
                  if (overlaps) {
                    group.push(actionPos)
                    addedToGroup = true
                    break
                  }
                }
                if (!addedToGroup) {
                  groups.push([actionPos])
                }
              })

              // Calculate width and left position for each action in groups
              type ActionWithLayout = ActionWithPosition & {
                left: number | string
                width: number | string
              }

              const actionsWithLayout: ActionWithLayout[] =
                actionsWithPositions.map((actionPos) => {
                  const group = groups.find((g) => g.includes(actionPos))
                  if (!group || group.length === 1) {
                    return {
                      ...actionPos,
                      left: 40,
                      width: 'calc(100% - 48px)'
                    }
                  }

                  const groupIndex = group.indexOf(actionPos)
                  const groupSize = group.length
                  const gap = 4
                  const availableWidth = 'calc(100% - 48px)'
                  const totalGaps = (groupSize - 1) * gap
                  const itemWidth = `calc((${availableWidth} - ${totalGaps}px) / ${groupSize})`
                  const left = `calc(40px + ${groupIndex} * (${itemWidth} + ${gap}px))`

                  return {
                    ...actionPos,
                    left,
                    width: itemWidth
                  }
                })

              return actionsWithLayout.map(
                ({
                  action,
                  actionDate,
                  actionEndDate,
                  topPercent,
                  height,
                  left,
                  width
                }) => (
                  <ActionEventBlock
                    key={action.id}
                    action={action}
                    actionDate={actionDate}
                    actionEndDate={actionEndDate}
                    topPercent={topPercent}
                    height={height}
                    left={left}
                    width={width}
                    totalHeight={totalHeight}
                    user={user}
                    legacyUrl={legacyUrl}
                  />
                )
              )
            })()}
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <DashboardBlockLayout {...props}>
        <Alert
          variation={AlertVariation.Danger}
          message="Erreur lors du chargement du calendrier"
          onDismiss={false}
        />
      </DashboardBlockLayout>
    )
  }

  if (isLoading || isUserIdsLoading) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <div className={calendarContainer}>
          <Shimmer width="100%" height={400} />
        </div>
      </DashboardBlockLayout>
    )
  }

  return (
    <DashboardBlockLayout {...props} actions={actions}>
      <div className={calendarContainer}>
        {renderDayTimetable(firstDay, actionsByDay.day1)}
        {renderDayTimetable(secondDay, actionsByDay.day2)}
      </div>
    </DashboardBlockLayout>
  )
}
